/** The frame every signed-out page shares: sign-in and its second-factor steps, joining a workspace,
 * resetting a password, and the state where the session could not be read.
 *
 * These pages had three stylesheets that said in their own headers they were one journey, and a
 * brand block copied into each of three screens. This is the one place that draws the page, the
 * brand and the card, so the next signed-out page cannot be a fourth styling.
 *
 * Four things are the point of it, and each is asserted by `AuthLayout.test.tsx`:
 *
 * - **An error is an icon and words** (`AuthNotice`), and a live region: colour is never the only
 *   signal, and a refusal that appears silently is one a screen-reader user never learns of.
 * - **The card's title is the page's `h1`.** These pages sit outside the shell, so nothing above
 *   them provides one. It takes focus when a step changes (`headingRef`), so a keyboard user is not
 *   left on a button that no longer exists.
 * - **The brand mark is the rail's**, as a letter on the accent - the product's name, not a glyph.
 * - **No control is added here.** This file draws; it decides nothing about who may sign in, what a
 *   refusal says or what is sent. Those stay on the screens that own them.
 */

import type { ReactNode, Ref } from "react";

import styles from "./AuthLayout.module.css";

/** The page: the dark brand surface, the lockup, and one centred column. */
export function AuthPage({ children }: { children: ReactNode }) {
  return (
    <main className={styles.page} tabIndex={-1}>
      <div className={styles.column}>
        <div className={styles.brand}>
          <div className={styles.lockup}>
            <span className={styles.mark} aria-hidden="true">
              E
            </span>
            <span className={styles.wordmark}>EmitIQ</span>
          </div>
          <span className={styles.tag}>Carbon compliance · California and the UK</span>
        </div>
        {children}
      </div>
    </main>
  );
}

interface AuthCardProps {
  /** The page's `h1`. */
  title: string;
  /** A sentence beside the title, a sibling of it rather than inside it - inside, it would join the
   * heading's accessible name and the heading list would stop being a list of short labels. */
  sub?: string;
  /** Which step this is, for the screens whose tests and browser checks key on it. */
  stage?: string;
  /** Focus target when the step changes. */
  headingRef?: Ref<HTMLHeadingElement>;
  /** Rendered between the title and the body, so a refusal is on screen without scrolling. */
  notice?: ReactNode;
  children: ReactNode;
}

export function AuthCard({ title, sub, stage, headingRef, notice, children }: AuthCardProps) {
  return (
    <section className={styles.card} data-stage={stage}>
      <header className={styles.head}>
        <h1 className={styles.title} tabIndex={-1} ref={headingRef}>
          {title}
        </h1>
        {sub ? <p className={styles.sub}>{sub}</p> : null}
      </header>
      <div className={styles.body}>
        {notice}
        {children}
      </div>
    </section>
  );
}

/** An inline error: an icon, then words. The icon is decorative (`aria-hidden`) because the words
 * carry the meaning; it is there so the signal is not colour alone. `id` lets the field it is about
 * point at it with `aria-describedby`. */
export function AuthNotice({
  children,
  id,
  testId,
}: {
  children: ReactNode;
  id?: string;
  testId?: string;
}) {
  return (
    <div className={styles.notice} role="alert" id={id} data-testid={testId}>
      <svg className={styles.noticeIcon} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M10 1.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17Zm-1 4.5h2v5.5H9V6Zm0 7h2v2H9v-2Z" />
      </svg>
      <div className={styles.noticeText}>{children}</div>
    </div>
  );
}

/** A labelled field. The label wraps the input, so the name is the visible text. */
export function AuthField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
    </label>
  );
}
